"use client";

import { c as _c } from "react/compiler-runtime";
import { useInView } from "@/hooks/useInView";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useOptimizedVideo } from "@/hooks/useOptimizedVideo";
import { useHlsPlayer } from "@/hooks/useHlsPlayer";
import { PlayIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import React, { useEffect } from "react";
import { InlineMediaActions } from "./MediaActionToolbar";
import { useMediaActions } from "./useMediaActions";
interface Props {
  readonly src: string;
  readonly disableAutoPlay?: boolean | undefined;
  readonly fillContainer?: boolean | undefined;
}
function DropListItemContentMediaVideo(t0) {
  const $ = _c(50);
  const {
    src,
    disableAutoPlay: t1,
    fillContainer: t2
  } = t0;
  const disableAutoPlay = t1 === undefined ? false : t1;
  const fillContainer = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      threshold: 0.1
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [wrapperRef, inView] = useInView(t3);
  const {
    isApp
  } = useDeviceInfo();
  let t4;
  if ($[1] !== src) {
    t4 = {
      url: src,
      fallbackFileName: "video",
      dialogTitle: "Save video"
    };
    $[1] = src;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      pollInterval: 10000,
      maxRetries: 8,
      preferHls: true,
      exponentialBackoff: false
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    playableUrl,
    isHls
  } = useOptimizedVideo(src, t5);
  const t6 = inView && !isApp && !disableAutoPlay;
  let t7;
  if ($[4] !== isHls || $[5] !== playableUrl || $[6] !== src || $[7] !== t6) {
    t7 = {
      src: playableUrl,
      isHls,
      fallbackSrc: src,
      autoPlay: t6
    };
    $[4] = isHls;
    $[5] = playableUrl;
    $[6] = src;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    videoRef,
    isLoading
  } = useHlsPlayer(t7);
  const showNativeControls = !isApp;
  const shouldAutoPlay = inView && !isApp && !disableAutoPlay;
  let t8;
  let t9;
  if ($[9] !== isApp || $[10] !== isLoading || $[11] !== shouldAutoPlay || $[12] !== videoRef) {
    t8 = () => {
      const videoEl = videoRef.current;
      if (!videoEl || isLoading) {
        return;
      }
      if (shouldAutoPlay) {
        videoEl.muted = true;
        if (!isApp) {
          videoEl.play().catch(_temp);
        }
      } else {
        videoEl.pause();
        videoEl.muted = true;
      }
    };
    t9 = [shouldAutoPlay, isApp, isLoading, videoRef];
    $[9] = isApp;
    $[10] = isLoading;
    $[11] = shouldAutoPlay;
    $[12] = videoRef;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[15] !== videoRef) {
    t10 = () => {
      const videoEl_0 = videoRef.current;
      if (!videoEl_0) {
        return;
      }
      videoEl_0.setAttribute("webkit-playsinline", "true");
      videoEl_0.setAttribute("x5-playsinline", "true");
    };
    t11 = [videoRef];
    $[15] = videoRef;
    $[16] = t10;
    $[17] = t11;
  } else {
    t10 = $[16];
    t11 = $[17];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[18] !== isApp || $[19] !== videoRef) {
    t12 = () => {
      if (!isApp) {
        return;
      }
      const pauseWhenFullscreenCloses = () => {
        const videoEl_1 = videoRef.current;
        if (!videoEl_1 || document.fullscreenElement === videoEl_1) {
          return;
        }
        videoEl_1.pause();
      };
      document.addEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      return () => {
        document.removeEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      };
    };
    t13 = [isApp, videoRef];
    $[18] = isApp;
    $[19] = videoRef;
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  useEffect(t12, t13);
  let t14;
  if ($[22] !== videoRef) {
    t14 = () => {
      const videoEl_2 = videoRef.current;
      if (!videoEl_2) {
        return;
      }
      videoEl_2.play().catch(_temp2);
      videoEl_2.requestFullscreen().catch(_temp3);
    };
    $[22] = videoRef;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const enterFullscreenAndPlay = t14;
  const t15 = fillContainer ? "tw-h-full tw-max-h-full" : "tw-max-h-64 tw-min-h-[200px]";
  let t16;
  if ($[24] !== t15) {
    t16 = clsx("tw-group tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-bg-black", t15);
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== enterFullscreenAndPlay || $[27] !== isApp) {
    t17 = () => {
      if (!isApp) {
        return;
      }
      enterFullscreenAndPlay();
    };
    $[26] = enterFullscreenAndPlay;
    $[27] = isApp;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = fillContainer ? "tw-h-full tw-max-h-full" : "tw-h-auto tw-max-h-64";
  let t19;
  if ($[29] !== t18) {
    t19 = clsx("tw-w-full tw-rounded-xl tw-object-contain", t18);
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== showNativeControls || $[32] !== t17 || $[33] !== t19 || $[34] !== videoRef) {
    t20 = <video ref={videoRef} onClick={t17} playsInline={true} controls={showNativeControls} controlsList="noplaybackrate" autoPlay={false} muted={true} loop={true} className={t19}>Your browser does not support the video tag.</video>;
    $[31] = showNativeControls;
    $[32] = t17;
    $[33] = t19;
    $[34] = videoRef;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== enterFullscreenAndPlay || $[37] !== isApp) {
    t21 = isApp && <button type="button" aria-label="Play video" title="Play video" onClick={event => {
      event.stopPropagation();
      enterFullscreenAndPlay();
    }} className="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-20 tw-flex tw-size-16 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-700/75 tw-text-white tw-shadow-lg tw-shadow-black/30 tw-backdrop-blur-sm"><PlayIcon className="tw-ml-1 tw-size-8" aria-hidden="true" /></button>;
    $[36] = enterFullscreenAndPlay;
    $[37] = isApp;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== downloadMedia || $[40] !== isDownloading || $[41] !== openLabel || $[42] !== openMedia) {
    t22 = <InlineMediaActions variant="video" onDownload={downloadMedia} onOpen={openMedia} openLabel={openLabel} isDownloading={isDownloading} />;
    $[39] = downloadMedia;
    $[40] = isDownloading;
    $[41] = openLabel;
    $[42] = openMedia;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t16 || $[45] !== t20 || $[46] !== t21 || $[47] !== t22 || $[48] !== wrapperRef) {
    t23 = <div ref={wrapperRef} className={t16}>{t20}{t21}{t22}</div>;
    $[44] = t16;
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
    $[48] = wrapperRef;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  return t23;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
export default React.memo(DropListItemContentMediaVideo);